/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import {
  ElementRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ArrowUp, Paperclip, Square } from 'lucide-react';

import type { ChatSidebarMode } from '@/contexts/ChatSidebarContextDef';
import { useChatSidebar } from '@/contexts/useChatSidebar';
import { useAtProductDropdown } from '@/hooks/useAtProductDropdown';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useProducts } from '@/hooks/useProducts';
import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/Button';
import { Popover, PopoverAnchor } from '@/components/ui/popover';

import { ContextSelectionCard } from '@/components/chat/ContextSelectionCard';
import type { ContextSelectionItem } from '@/components/chat/ContextSelectionContext';
import {
  ContextSelectionType,
  useContextSelection,
} from '@/components/chat/ContextSelectionContext';
import { FileAttachmentCard } from '@/components/chat/FileAttachmentCard';
import { SlateMessageInput } from '@/components/chat/SlateMessageInput';
import {
  ToolItem,
  ToolVersionItem,
  useTools,
} from '@/components/chat/chat-context-menu/hooks/useTools';
import { ToolVersionSelector } from '@/components/chat/chat-context-menu/tools/ToolVersionSelector';
import { FILE_INPUT_ACCEPT } from '@/components/chat/fileAttachmentConstants';
import type { FileContext } from '@/components/chat/parseAttachedFiles';
import type { UseFileAttachmentsReturn } from '@/components/chat/useFileAttachments';
import { useFileAttachments } from '@/components/chat/useFileAttachments';

import {
  AtProductPopoverMenuContent,
  AtProductPopoverTrigger,
} from './AtProductPopover';

interface MessageInputProps {
  onSendMessage: (
    message: string,
    contextSelection?: ContextSelectionItem[],
    fileContexts?: FileContext[]
  ) => Promise<boolean>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  value?: string;
  hideWarningMessage?: boolean;
  onChange?: (value: string) => void;
  fixedHeight?: boolean;
  mode?: ChatSidebarMode;
  fileAttachments?: UseFileAttachmentsReturn;
  isResponding?: boolean;
  onStopResponding?: () => void;
}

export const MessageInput = ({
  disabled = false,
  mode,
  placeholder = 'Type a message...',
  className = '',
  value,
  hideWarningMessage = false,
  fixedHeight,
  onChange,
  onSendMessage,
  fileAttachments: fileAttachmentsProp,
  isResponding = false,
  onStopResponding,
}: MessageInputProps) => {
  const editorRef = useRef<ElementRef<typeof SlateMessageInput> | null>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const commandRef = useRef<HTMLDivElement>(null);
  const { refetch } = useProducts();
  const { flush: flushDebounce } = useDebouncedCallback(refetch, 1000);
  const [uncontrolledValue, setUncontrolledValue] = useState<string>('');
  const isControlled = value !== undefined && onChange !== undefined;
  const inputValue = (isControlled ? value : uncontrolledValue) as string;
  const sendButtonId = useId();
  const { contextSelection, setContextSelection, setOnSelectItem } =
    useContextSelection();
  const { tools } = useTools();
  const [isToolSheetOpen, setIsToolSheetOpen] = useState(false);
  const [toolItem, setToolItem] = useState<ToolItem | null>(null);
  const [activeToolSelection, setActiveToolSelection] =
    useState<ContextSelectionItem | null>(null);
  const { isOpen: isChatSidebarOpen } = useChatSidebar();
  const fallbackFileAttachments = useFileAttachments();
  const fileAttachments = fileAttachmentsProp ?? fallbackFileAttachments;
  const {
    attachedFiles,
    addFiles,
    removeFile,
    retryFile,
    markFilesSent,
    restoreFilesToParsed,
    fileInputRef,
    openFilePicker,
    canSubmitFiles,
    getCanSubmitFiles,
    parsedFileContexts,
  } = fileAttachments;
  const isFileAttachmentEnabled = !!fileAttachmentsProp;

  const canSend = useMemo(() => {
    return !!inputValue.trim() && !disabled && !isResponding && canSubmitFiles;
  }, [inputValue, disabled, isResponding, canSubmitFiles]);

  const setInputValue = useCallback(
    (value: string) => {
      if (isControlled) {
        onChange?.(value);
      } else {
        setUncontrolledValue(value);
      }
    },
    [isControlled, onChange]
  );

  const {
    open: openAtDropdown,
    source: atOpenSource,
    setSource: setAtOpenSource,
    onOpenChange,
    onKeyDown,
    setOpen,
  } = useAtProductDropdown({
    commandRef,
    focusInput: () => editorRef.current?.focus(),
  });

  useEffect(() => {
    if (!mode || isChatSidebarOpen || !openAtDropdown) {
      return;
    }

    setOpen(false);
    setAtOpenSource(null);
  }, [isChatSidebarOpen, mode, openAtDropdown, setAtOpenSource, setOpen]);

  useEffect(() => {
    if (!isChatSidebarOpen) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      editorRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, [isChatSidebarOpen]);

  useMemo(() => {
    if (value !== undefined && !onChange) {
      console.warn('MessageInput is controlled but missing onChange');
    }
  }, [value, onChange]);

  const isSendingRef = useRef(false);
  const inputValueRef = useRef(inputValue);
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  const handleOnSend = useCallback(async () => {
    if (isSendingRef.current) {
      return;
    }

    const rawMessage = inputValue.trim();
    if (!rawMessage || disabled || isResponding || !getCanSubmitFiles()) {
      return;
    }

    isSendingRef.current = true;
    setInputValue('');

    const fileContextsToSend =
      parsedFileContexts.length > 0 ? parsedFileContexts : undefined;

    const sentFileIds = fileContextsToSend ? markFilesSent() : [];

    const restoreDraftIfEmpty = () => {
      if (!inputValueRef.current.trim()) {
        setInputValue(rawMessage);
      }
    };

    try {
      const success = await onSendMessage(
        rawMessage,
        contextSelection,
        fileContextsToSend
      );

      if (success === false) {
        restoreDraftIfEmpty();
        restoreFilesToParsed(sentFileIds);
      }
    } catch {
      restoreDraftIfEmpty();
      restoreFilesToParsed(sentFileIds);
    } finally {
      isSendingRef.current = false;
    }
  }, [
    inputValue,
    disabled,
    getCanSubmitFiles,
    isResponding,
    setInputValue,
    contextSelection,
    parsedFileContexts,
    onSendMessage,
    markFilesSent,
    restoreFilesToParsed,
  ]);

  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        flushDebounce();
      }

      onOpenChange(open);
    },
    [flushDebounce, onOpenChange]
  );

  const handleAtQueryChange = useCallback(
    (query: string | null) => {
      if (disabled) {
        return;
      }
      if (query === null) {
        if (atOpenSource === 'textarea') {
          setOpen(false);
          setAtOpenSource(null);
        }
        return;
      }

      if (!openAtDropdown) {
        flushDebounce();
        setOpen(true);
      }

      setAtOpenSource('textarea');
    },
    [
      atOpenSource,
      disabled,
      flushDebounce,
      openAtDropdown,
      setAtOpenSource,
      setOpen,
    ]
  );

  const handleContextSelect = useCallback(
    (item: ContextSelectionItem) => {
      if (disabled) {
        return;
      }

      setOpen(false);
      setAtOpenSource(null);
      editorRef.current?.insertContextToken({
        id: item.id,
        label: item.title,
        type: item.type,
      });
    },
    [disabled, setAtOpenSource, setOpen]
  );

  const toolById = useMemo(() => {
    return new Map<string, ToolItem>(
      tools.map((item: ToolItem) => [item.id, item])
    );
  }, [tools]);

  const toolByVersionId = useMemo(() => {
    const map = new Map<string, ToolItem>();
    tools.forEach((tool: ToolItem) => {
      tool.versions.forEach((version: ToolVersionItem) => {
        map.set(version.id, tool);
      });
    });
    return map;
  }, [tools]);

  const resolveToolForSelection = useCallback(
    (item: ContextSelectionItem) => {
      if (item.type !== ContextSelectionType.Tool) {
        return null;
      }

      return toolById.get(item.id) ?? toolByVersionId.get(item.id) ?? null;
    },
    [toolById, toolByVersionId]
  );

  const handleToolCardClick = useCallback(
    (item: ContextSelectionItem) => {
      if (item.type !== ContextSelectionType.Tool) {
        return;
      }

      const resolvedTool = resolveToolForSelection(item);

      if (!resolvedTool || resolvedTool.versionsCount <= 1) {
        return;
      }

      setActiveToolSelection(item);
      setToolItem(resolvedTool);
      setIsToolSheetOpen(true);
    },
    [resolveToolForSelection]
  );

  const toolVersionsForSheet = useMemo(() => {
    if (!toolItem) {
      return [];
    }

    const selectedIds = new Set(activeToolSelection?.selectedVersions ?? []);

    return toolItem.versions.map((version: ToolVersionItem) => ({
      ...version,
      editedAt: version.editedAt ?? '',
      checked: selectedIds.has(version.id),
    }));
  }, [activeToolSelection, toolItem]);

  const handleToolVersionConfirm = useCallback(
    (selectedIds: string[]) => {
      setIsToolSheetOpen(false);

      if (!activeToolSelection) {
        return;
      }

      if (selectedIds.length === 0) {
        setContextSelection((prev: ContextSelectionItem[]) =>
          prev.filter(
            (item: ContextSelectionItem) =>
              item.id !== activeToolSelection.id ||
              item.type !== activeToolSelection.type
          )
        );
        return;
      }

      setContextSelection((prev: ContextSelectionItem[]) =>
        prev.map((item: ContextSelectionItem) =>
          item.id === activeToolSelection.id &&
          item.type === activeToolSelection.type
            ? { ...item, selectedVersions: selectedIds }
            : item
        )
      );
    },
    [activeToolSelection, setContextSelection]
  );

  useEffect(() => {
    setOnSelectItem(handleContextSelect);
    return () => setOnSelectItem(null);
  }, [handleContextSelect, setOnSelectItem]);

  const contextMenuSource = atOpenSource === 'button' ? 'button' : 'textarea';
  const toolSelections = useMemo(
    () =>
      contextSelection.filter(
        (item: ContextSelectionItem) => item.type === ContextSelectionType.Tool
      ),
    [contextSelection]
  );
  const pendingFiles = useMemo(
    () => attachedFiles.filter((attached) => attached.status !== 'sent'),
    [attachedFiles]
  );

  return (
    <div
      data-testid="stella-message-input-root"
      className={cn('w-full pt-0', {
        'p-6': mode === 'sidebar',
        'p-4': mode !== 'sidebar',
      })}
    >
      <div
        className={cn('mx-auto', className)}
        data-testid="stella-message-input-inner"
      >
        {isFileAttachmentEnabled && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={FILE_INPUT_ACCEPT}
            className="hidden"
            onChange={(event) => {
              if (event.target.files) {
                addFiles(event.target.files);
              }
              event.target.value = '';
            }}
          />
        )}
        <Popover open={openAtDropdown} onOpenChange={handleOnOpenChange}>
          <PopoverAnchor asChild>
            <div
              ref={inputAreaRef}
              data-testid="stella-message-input-container"
              className="flex flex-col gap-2 pb-2 border border-input overflow-hidden focus-within:border-primary bg-background rounded-2xl focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background"
            >
              {(toolSelections.length > 0 || pendingFiles.length > 0) && (
                <div
                  data-testid="stella-context-selection-container"
                  className="flex w-full min-w-0 shrink-0 gap-2 items-start pl-3 pr-2 pt-3 overflow-x-auto scrollbar-hide touch-pan-x"
                >
                  {toolSelections.map((item: ContextSelectionItem) => {
                    const toolForItem = resolveToolForSelection(item);
                    const canOpenSelector =
                      item.type === ContextSelectionType.Tool &&
                      (toolForItem?.versionsCount ?? 0) > 1;

                    return (
                      <ContextSelectionCard
                        key={`${item.type}-${item.id}`}
                        id={item.id}
                        type={item.type}
                        title={item.title}
                        selectedVersions={item.selectedVersions}
                        lastEditLabel={item.lastEditLabel}
                        onClick={
                          canOpenSelector
                            ? () => handleToolCardClick(item)
                            : undefined
                        }
                      />
                    );
                  })}
                  {pendingFiles.map((attached) => (
                    <FileAttachmentCard
                      key={attached.id}
                      file={attached.file}
                      status={attached.status}
                      error={attached.error}
                      onRemove={() => removeFile(attached.id)}
                      onRetry={() => retryFile(attached.id)}
                    />
                  ))}
                </div>
              )}

              <div className="flex min-h-0 flex-1 flex-col">
                <SlateMessageInput
                  ref={editorRef}
                  value={inputValue}
                  onChangeValue={setInputValue}
                  onSubmit={handleOnSend}
                  onAtQueryChange={handleAtQueryChange}
                  onKeyDown={onKeyDown}
                  placeholder={placeholder}
                  disabled={disabled}
                  fixedHeight={fixedHeight}
                  ariaExpanded={openAtDropdown}
                />
              </div>

              <div className="flex shrink-0 gap-2 justify-between">
                <div className="flex items-center">
                  {isFileAttachmentEnabled && (
                    <Button
                      onClick={openFilePicker}
                      variant="outline"
                      className="h-7 w-7 ml-2 flex-shrink-0"
                      size="icon"
                      aria-label="Attach files"
                      disabled={disabled || isResponding}
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                  )}
                  <AtProductPopoverTrigger
                    open={openAtDropdown}
                    setSource={setAtOpenSource}
                  />
                </div>

                {isResponding && onStopResponding ? (
                  <Button
                    onClick={onStopResponding}
                    variant="default"
                    size="icon"
                    aria-label="Stop generating"
                    className="h-7 w-7 mr-2 flex-shrink-0"
                    title="Stop generating"
                  >
                    <Square className="h-3 w-3 fill-current" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleOnSend}
                    disabled={!canSend}
                    variant={canSend ? 'default' : 'secondary'}
                    size="icon"
                    aria-label="Send message"
                    className="h-7 w-7 mr-2 flex-shrink-0"
                    title="Send message"
                    id={sendButtonId}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </PopoverAnchor>
          <AtProductPopoverMenuContent
            source={contextMenuSource}
            buttonSide={mode ? 'left' : 'bottom'}
            commandRef={commandRef}
          />
        </Popover>

        {toolItem && (
          <ToolVersionSelector
            open={isToolSheetOpen}
            onOpenChange={setIsToolSheetOpen}
            onClose={() => setIsToolSheetOpen(false)}
            onConfirm={handleToolVersionConfirm}
            versions={toolVersionsForSheet}
            toolName={toolItem.name}
          />
        )}

        {!hideWarningMessage && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            Stella can make mistakes. Praxis doesn't use your data to train
            Stella for other users.
          </p>
        )}
      </div>
    </div>
  );
};
